import {
  FsId,
  Hermite,
  RawEventKind,
  RawEventOfKind,
  TimeStampMs,
  TouchId,
} from '@fullstory/recording-api';
import { FramePath, NodePath } from '@fullstory/renderer-shared';
import { assertExhaustive } from '@fullstory/typeutils';
import { DefaultMap } from '@fullstory/util/defaultmap';
import type { DocumentEvents } from './document_events';
import type { FramePoint } from './hermite_curves';
import { FrameHermiteCurve2d } from './hermite_curves';
import { ImmutableTimeline, Timeline } from './timeline/timeline';

/**
 * Strategies for visualizing pointer-like events.
 */
type PointerMode =
  | 'mouse-and-touch' // Behave as if mouse and touch events come from a real device.
  | 'touch-only'; // Behave as if only touch events come from a real device.

/**
 * Strategies for visualizing :hover.
 */
type HoverMode =
  | 'none' // Behave as if the user is using a device that doesn't generate :hover.
  | 'hover'; // Behave as if the user is using a :hover-capable device.

/**
 * A timeline for events with page scope - in other words, events which happen
 * "globally" within the page, rather than being associated with a specific
 * document.
 *
 * Note that all events are associated with a specific document at recording
 * time; this is just a consequence of the way that the recording script works.
 * The question is whether the underlying thing the events describe is
 * associated with a document. All mouse movements in all documents describe the
 * same mouse pointer, for example, so mouse movements are page-scope events.
 * Scroll position changes, on the other hand, are associated with a specific
 * element or document, so they are document-scope events, and they're handled
 * by {@link DocumentEventTimeline} instead of this class.
 */
export class PageEventTimeline {
  /**
   * @return the timeline for this page's root document. The value is undefined
   * when there is no active root document. That should normally only be the
   * case when we haven't received any raw events yet.
   */
  public get rootDocument(): ImmutableTimeline<DocumentEvents> {
    return this._rootDocument;
  }
  private readonly _rootDocument = new Timeline<DocumentEvents>();

  /** @return the timeline for the page's active {@link PointerMode}. */
  public get pointerMode(): ImmutableTimeline<PointerMode> {
    return this._pointerMode;
  }
  private readonly _pointerMode = new Timeline<PointerMode>();

  /** @return the timeline for the page's active {@link HoverMode}. */
  public get hoverMode(): ImmutableTimeline<HoverMode> {
    return this._hoverMode;
  }
  private readonly _hoverMode = new Timeline<HoverMode>();

  /** @return the timeline for the mouse's position. */
  public get mousePosition(): ImmutableTimeline<FramePoint> {
    return this._mousePosition;
  }
  private readonly _mousePosition = new Timeline<FramePoint>();

  /**
   * @return the timeline for the mouse button's "down" state; the value is
   * "true" on the parts of the timeline where the button is being clicked.
   */
  public get mouseDown(): ImmutableTimeline<boolean> {
    return this._mouseDown;
  }
  private readonly _mouseDown = new Timeline<boolean>();

  /**
   * @return the timeline for the document's hovered element. The value is
   * undefined when no element is hovered.
   */
  public get hoveredElement(): ImmutableTimeline<NodePath> {
    return this._hoveredElement;
  }
  private readonly _hoveredElement = new Timeline<NodePath>();

  /**
   * @return the timeline for the document's focused element. The value is
   * undefined when no element is focused.
   */
  public get focusedElement(): ImmutableTimeline<NodePath> {
    return this._focusedElement;
  }
  private readonly _focusedElement = new Timeline<NodePath>();

  /**
   * @return the timelines for the positions of each multitouch "finger". The
   * values are {@link FramePoint}s on the parts of the timeline where each
   * finger is "touching", and "undefined" where the finger is not touching.
   * This information must be combined with the {@link touchActive} timelines to
   * determine how each touch should be renderered; see the comments there for
   * more information.
   */
  public get touchPosition(): DefaultMap<TouchId, ImmutableTimeline<FramePoint>> {
    return this._touchPosition;
  }
  private readonly _touchPosition = new DefaultMap<TouchId, Timeline<FramePoint>>(
    () => new Timeline<FramePoint>(),
  );

  /**
   * @return the timelines for the state of each multitouch "finger". The values
   * are "true" on the parts of the timeline where each finger is "touching",
   * and "false" where the finger is not touching.
   *
   * TODO(sethf): In principle {@link touchPosition} already contains this
   * information, but in practice Safari reports touch movements for touches
   * which have already ended, so we'll need this separation unless we can find
   * a way to fix the problem at the recording script level.
   */
  public get touchActive(): DefaultMap<TouchId, ImmutableTimeline<boolean>> {
    return this._touchActive;
  }
  private readonly _touchActive = new DefaultMap<TouchId, Timeline<boolean>>(
    () => new Timeline<boolean>(),
  );

  public get fullscreen(): ImmutableTimeline<NodePath> {
    return this._fullscreen;
  }
  private readonly _fullscreen = new Timeline<NodePath>();

  addRootDocument(event: RawEventOfKind<'SET_FRAME_BASE'>, docEvents: DocumentEvents): void;
  addRootDocument(event: RawEventOfKind<'UNLOAD'>, docEvents: undefined): void;
  addRootDocument(
    event: RawEventOfKind<'SET_FRAME_BASE' | 'UNLOAD'>,
    docEvents: DocumentEvents | undefined,
  ): void {
    this._rootDocument.addEventWithValue(event, docEvents);
  }

  addMediaQueryChangeEvent(event: RawEventOfKind<'MEDIA_QUERY_CHANGE'>, _frameId: FramePath): void {
    switch (event.Args[0]) {
      case 'any-pointer': {
        const newMode = event.Args[1] == 'fine' ? 'mouse-and-touch' : 'touch-only';
        this._pointerMode.addEventWithValue(event, newMode);
        break;
      }

      case 'any-hover': {
        const newMode = event.Args[1] == 'hover' ? 'hover' : 'none';
        this._hoverMode.addEventWithValue(event, newMode);
        break;
      }
    }
  }

  addMouseEvent(
    event: RawEventOfKind<'MOUSEDOWN' | 'MOUSEUP' | 'MOUSEMOVE'>,
    frame: FramePath,
  ): void {
    const point: FramePoint = {
      frame,
      point: { x: event.Args[0], y: event.Args[1] },
    };
    this._mousePosition.addEventWithValue(event, point);

    switch (event.Kind) {
      case RawEventKind.MOUSEDOWN:
        this._mouseDown.addEventWithValue(event, true);
        break;
      case RawEventKind.MOUSEUP:
        this._mouseDown.addEventWithValue(event, false);
        break;
      case RawEventKind.MOUSEMOVE:
        break;
      default:
        assertExhaustive(event, `Unexpected mouse event: ${JSON.stringify(event)}`);
    }
  }

  addHoverEvent(event: RawEventOfKind<'MOUSEMOVE' | 'MOUSEMOVE_CURVE'>): void {
    let hoverFsId: FsId | undefined;
    switch (event.Kind) {
      case RawEventKind.MOUSEMOVE:
        hoverFsId = event.Args[2];
        break;
      case RawEventKind.MOUSEMOVE_CURVE:
        hoverFsId = event.Args[9];
        break;
      default:
        assertExhaustive(event, `Unexpected hover event: ${JSON.stringify(event)}`);
    }
    if (typeof hoverFsId === 'number') {
      const frame = new FramePath(event.FId, event.PIds);
      const path = new NodePath(frame, hoverFsId);
      this._hoveredElement.addEventWithValue(event, path);
    }
  }

  addFocusEvent(event: RawEventOfKind<'FOCUS' | 'BLUR'>): void {
    switch (event.Kind) {
      case RawEventKind.FOCUS: {
        const [fsid] = event.Args;
        const frame = new FramePath(event.FId, event.PIds);
        const path = new NodePath(frame, fsid);
        this._focusedElement.addEventWithValue(event, path);
        break;
      }
      case RawEventKind.BLUR:
        this._focusedElement.addEventWithValue(event, undefined);
        break;
      default:
        assertExhaustive(event, `Unexpected focus event: ${JSON.stringify(event)}`);
    }
  }

  addMouseCurveEvent(event: RawEventOfKind<'MOUSEMOVE_CURVE'>, frame: FramePath): void {
    const xCurve: Hermite = [event.Args[1], event.Args[2], event.Args[3], event.Args[4]];
    const yCurve: Hermite = [event.Args[5], event.Args[6], event.Args[7], event.Args[8]];
    const curveFunction = new FrameHermiteCurve2d(xCurve, yCurve, frame);
    const curveEnd: TimeStampMs = event.Args[0];
    this._mousePosition.addEventWithCurve(event, curveEnd, curveFunction, frame);
  }

  addTouchEvent(
    event: RawEventOfKind<'TOUCHSTART' | 'TOUCHMOVE' | 'TOUCHEND' | 'TOUCHCANCEL'>,
    frame: FramePath,
  ): void {
    // Update touch position.
    const posForTouchId = this._touchPosition.get(event.Args[0]);
    switch (event.Kind) {
      case RawEventKind.TOUCHSTART:
      case RawEventKind.TOUCHMOVE: {
        const point = { x: event.Args[1], y: event.Args[2] };
        posForTouchId.addEventWithValue(event, { frame, point });
        break;
      }

      case RawEventKind.TOUCHEND:
      case RawEventKind.TOUCHCANCEL: {
        posForTouchId.addEventWithValue(event, undefined);
        break;
      }

      default:
        assertExhaustive(event, `Unexpected touch event: ${JSON.stringify(event)}`);
    }

    // Update touch active state.
    const touchIdIsActive = this._touchActive.get(event.Args[0]);
    switch (event.Kind) {
      case RawEventKind.TOUCHSTART:
        touchIdIsActive.addEventWithValue(event, true);
        break;

      case RawEventKind.TOUCHEND:
      case RawEventKind.TOUCHCANCEL:
        touchIdIsActive.addEventWithValue(event, false);
        break;

      case RawEventKind.TOUCHMOVE:
        break; // No active state change.

      default:
        assertExhaustive(event, `Unexpected touch event: ${JSON.stringify(event)}`);
    }
  }

  addTouchCurveEvent(event: RawEventOfKind<'TOUCHMOVE_CURVE'>, frame: FramePath): void {
    const xCurve: Hermite = [event.Args[2], event.Args[3], event.Args[4], event.Args[5]];
    const yCurve: Hermite = [event.Args[6], event.Args[7], event.Args[8], event.Args[9]];
    const curveFunction = new FrameHermiteCurve2d(xCurve, yCurve, frame);
    const curveEnd: TimeStampMs = event.Args[0];
    this._touchPosition.get(event.Args[1]).addEventWithCurve(event, curveEnd, curveFunction, frame);
  }

  addFullscreenEvent(event: RawEventOfKind<'FULLSCREEN'>): void {
    const fsId = event.Args[0];
    const isFullscreen = event.Args[1];

    if (typeof fsId !== 'number') {
      return;
    }

    if (isFullscreen) {
      const frame = new FramePath(event.FId, event.PIds);
      const path = new NodePath(frame, fsId);
      this._fullscreen.addEventWithValue(event, path);
    } else {
      this._fullscreen.addEventWithValue(event, undefined);
    }
  }
}
